import React from "react";
import { Pill } from "../../components/analytics-kit";
import account from "../../data/account";
import { num, usd, elapsedFraction } from "./territory-common";
import { lapseLabel, lapseTone } from "./sales-api";

// Who sees the cockpit (§9): owner, administrator and the `sales` Cognito group.
// Targets write stays owner / administrator (the store_targets rule).
export function isSalesUser() {
  const groups = ((account.get() || {}).groups || []).map(g => String(g).toLowerCase());
  return ["owner", "administrator", "root", "sales"].some(g => groups.includes(g));
}

/*
 * Shared pieces of the Sales executive view (SALES_EXECUTIVE_VIEW.md §5,
 * mockup sales-executive-view-2026-10-06.html): the 12-point sparkline with an
 * optional dashed plan line, the attainment bar, the pace chip, the health
 * chips and the compact money formatter. Used by the cockpit, the unified
 * targets grid and the Plan card on the detail pages.
 */

// $4.82M / $612K / $4,138: the ribbon and grid read in short money.
export function usdK(v) {
  const x = Number(v) || 0;
  if (!x) return "$0";
  const a = Math.abs(x);
  if (a >= 1e6) return `$${(x / 1e6).toFixed(2)}M`;
  if (a >= 1e4) return `$${Math.round(x / 1e3)}K`;
  return `$${Math.round(x).toLocaleString("en-US")}`;
}
// Rounded dollars, no cents: $2,629 (the cockpit never shows cents).
export function usd0(v) {
  const x = Math.round(Number(v) || 0);
  return `${x < 0 ? "-" : ""}$${Math.abs(x).toLocaleString("en-US")}`;
}
// "last order 74 days ago (usually every 30)" from a dealer health object.
export function lastOrderText(h) {
  if (!h || h.daysSinceOrder == null) return "";
  const d = Number(h.daysSinceOrder);
  const when = d <= 0 ? "last order today" : d === 1 ? "last order yesterday" : `last order ${num(d)} days ago`;
  return h.cadenceDays != null ? `${when} (usually every ${num(h.cadenceDays)})` : when;
}
export const pct0 = r => r == null || !isFinite(r) ? "" : `${Math.round(Number(r) * 100)}%`;
export const ratioDelta = (cur, prev) => {
  const c = Number(cur) || 0;
  const p = Number(prev) || 0;
  if (p <= 0) return null;
  const pct = (c - p) / p * 100;
  return {
    dir: pct > 0.05 ? "up" : pct < -0.05 ? "down" : "flat",
    pct
  };
};

/** Inline SVG sparkline (12 points by convention); `plan` draws a dashed grey line on the same scale. */
export function Spark({
  values,
  plan,
  w = 90,
  h = 22
}) {
  const vals = (values || []).map(v => Number(v) || 0);
  if (vals.length < 2) return /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "\u2014");
  const pl = (plan || []).map(v => Number(v) || 0);
  const mx = Math.max(...vals, ...pl, 1);
  const pts = a => a.map((v, i) => `${(i / (a.length - 1) * w).toFixed(1)},${(h - v / mx * (h - 2) - 1).toFixed(1)}`).join(" ");
  return /*#__PURE__*/React.createElement("svg", {
    className: "sx-spark",
    width: w,
    height: h,
    viewBox: `0 0 ${w} ${h}`,
    "aria-hidden": "true"
  }, pl.length > 1 ? /*#__PURE__*/React.createElement("polyline", {
    points: pts(pl),
    fill: "none",
    stroke: "var(--plk-chart-previous, #c2cfe0)",
    strokeWidth: "1.5",
    strokeDasharray: "3 2"
  }) : null, /*#__PURE__*/React.createElement("polyline", {
    points: pts(vals),
    fill: "none",
    stroke: "var(--plk-chart-current, #2f80ed)",
    strokeWidth: "1.6"
  }));
}

/** Attainment bar: fill to min(100%, ratio) with a tone by pace (ok / warn / bad) and the percent beside it. */
export function AttainBar({
  ratio,
  tone
}) {
  if (ratio == null || !isFinite(ratio)) return /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "\u2014");
  const t = tone || (ratio >= 1 ? "" : ratio >= 0.9 ? "warn" : "bad");
  return /*#__PURE__*/React.createElement("span", {
    className: "sx-bar-wrap"
  }, /*#__PURE__*/React.createElement("span", {
    className: `sx-bar ${t}`
  }, /*#__PURE__*/React.createElement("i", {
    style: {
      width: `${Math.min(100, Math.round(ratio * 100))}%`
    }
  })), pct0(ratio));
}

/** Pace to window end as a coloured number (green at or over plan, muted 90 to 100, red under). */
export function PaceText({
  ratio
}) {
  if (ratio == null || !isFinite(ratio)) return /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "\u2014");
  const cls = ratio >= 1 ? "up" : ratio >= 0.9 ? "flat" : "down";
  return /*#__PURE__*/React.createElement("span", {
    className: `wa-d ${cls}`
  }, pct0(ratio));
}
export const paceTone = ratio => ratio == null ? "" : ratio >= 1 ? "" : ratio >= 0.9 ? "warn" : "bad";

/** Rep health: how many of the rep's dealers are ordering later than usual (overdue or stopped), with their yearly sales. */
export function HealthChips({
  health
}) {
  if (!health) return /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "\u2014");
  const risk = (Number(health.atRisk) || 0) + (Number(health.lapsing) || 0);
  if (!risk) return /*#__PURE__*/React.createElement("span", {
    className: "hint"
  }, "none");
  return /*#__PURE__*/React.createElement("span", {
    className: "sx-chips",
    title: "Dealers of this rep ordering later than they usually do, and what they buy in a year"
  }, /*#__PURE__*/React.createElement(Pill, {
    tone: "danger"
  }, num(risk)), /*#__PURE__*/React.createElement("span", {
    className: "hint"
  }, health.revenueAtRisk ? `${usdK(health.revenueAtRisk)} a year` : ""));
}

/** Dealer health: the plain-language band pill; the hover says what it is measured against. */
export function BandChip({
  health
}) {
  const band = health && health.band;
  if (!band) return /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "\u2014");
  return /*#__PURE__*/React.createElement("span", {
    className: "sx-chips",
    title: lastOrderText(health) ? `Compared with how often this dealer usually orders: ${lastOrderText(health)}` : "Compared with how often this dealer usually orders"
  }, /*#__PURE__*/React.createElement(Pill, {
    tone: lapseTone(band)
  }, lapseLabel(band)));
}

/** State chips: click opens Market detail. */
export function StateChips({
  states,
  onOpen,
  max = 6
}) {
  const list = (states || []).slice(0, max);
  if (!list.length) return /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "\u2014");
  return /*#__PURE__*/React.createElement("span", {
    className: "sx-chips"
  }, list.map(s => /*#__PURE__*/React.createElement("button", {
    key: s,
    className: "sx-state",
    onClick: e => {
      e.stopPropagation();
      onOpen && onOpen(s);
    },
    title: `Open ${s} market detail`
  }, s)), (states || []).length > max ? /*#__PURE__*/React.createElement("span", {
    className: "hint"
  }, "+", (states || []).length - max) : null);
}
export function CategoryChips({
  categories,
  onOpen
}) {
  const list = (categories || []).slice(0, 3);
  if (!list.length) return /*#__PURE__*/React.createElement("span", {
    className: "dim"
  }, "\u2014");
  return /*#__PURE__*/React.createElement("span", {
    className: "sx-chips"
  }, list.map((c, i) => /*#__PURE__*/React.createElement("button", {
    key: `${c.category}|${i}`,
    className: "wa-pill neutral sx-cat",
    onClick: e => {
      e.stopPropagation();
      onOpen && onOpen(c);
    }
  }, c.category, c.share != null ? ` ${pct0(c.share)}` : "")));
}

/** "6 of 13 weeks elapsed" for the window, from the shared elapsed-fraction rule. */
export function elapsedLabel(range) {
  const f = elapsedFraction(range);
  if (f >= 0.999) return "complete window";
  if (range === "this_year") return `${Math.round(f * 52)} of 52 weeks elapsed`;
  if (range === "this_quarter") return `${Math.round(f * 13)} of 13 weeks elapsed`;
  if (range === "this_month") return `${Math.round(f * 30)} of ${new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0).getDate()} days elapsed`;
  return `${Math.round(f * 100)}% elapsed`;
}

/** Window label for the target column header: "Target Q4", "Target 2026", "Target Oct". */
export function targetWindowLabel(range) {
  const now = new Date();
  if (/quarter/.test(range)) {
    let q = Math.floor(now.getMonth() / 3) + 1;
    if (range === "last_quarter") q = q === 1 ? 4 : q - 1;
    return `Target Q${q}`;
  }
  if (/year/.test(range)) return `Target ${now.getFullYear() - (range === "last_year" ? 1 : 0)}`;
  if (/month/.test(range)) {
    const m = new Date(now.getFullYear(), now.getMonth() - (range === "last_month" ? 1 : 0), 1);
    return `Target ${m.toLocaleDateString("en-US", {
      month: "short"
    })}`;
  }
  return "Target";
}

/** Short money with the delta line under it, as the mockup's revenue cells. */
export function MoneyDelta({
  value,
  prev,
  Delta
}) {
  const d = ratioDelta(value, prev);
  return /*#__PURE__*/React.createElement("span", {
    className: "sx-money"
  }, usdK(value), Delta && d ? /*#__PURE__*/React.createElement("span", {
    className: "sx-sub"
  }, /*#__PURE__*/React.createElement(Delta, {
    d: d
  })) : null);
}
export { num, usd };